import { catalogDisplay, mustGetCatalogService, type CatalogService } from '@/lib/service-catalog'
import caseStudies from '@/data/case-studies.json'

export type AuditLogo = {
  src: string
  alt: string
}

export type SmartAudit = {
  id: string
  chip: string
  name: string
  blurb: string
  logos: AuditLogo[]
  price: string
  days: string
  href: string
  cta: string
}

export type DirectoryItem = Pick<SmartAudit, 'id' | 'chip' | 'logos'>

const CRM_IDS = [
  'salesforce-audit',
  'hubspot-audit',
  'zoho-audit',
  'attio-audit',
  'activecampaign-audit',
  'marketo-audit',
  'jobtread-audit',
  'workiz-audit',
  'jobber-audit',
] as const

const MARKETING_IDS = [
  'tracking-audit',
  'consent-privacy-audit',
  'ppc-audit',
  'seo-audit',
  'ai-visibility-audit',
  'cro-audit',
] as const

function catalogLogos(item: CatalogService): AuditLogo[] {
  const srcs = item.logos?.length ? item.logos : [item.logo]
  return srcs.map((src) => ({ src, alt: item.name }))
}

function chipName(item: CatalogService) {
  if (item.subcat === 'crm') {
    return item.name
      .replace(/\s+Attribution & Pipeline Audit$/i, '')
      .replace(/\s+Marketing Audit$/i, '')
      .replace(/\s+CRM Audit$/i, '')
      .replace(/\s+Audit$/i, '')
  }
  return item.name.replace(/\s+Compliance Audit$/i, '')
}

function auditCta(item: CatalogService, chip: string) {
  if (item.href && item.href !== '/audit' && !item.href.startsWith('#')) {
    return { href: item.href, cta: `View ${chip} Services` }
  }
  return { href: '#get-quote', cta: `Request ${chip} Audit` }
}

function fromCatalog(id: string): SmartAudit {
  const item = mustGetCatalogService(id)
  const chip = chipName(item)
  return {
    id: item.id,
    chip,
    name: item.name,
    blurb: item.description,
    logos: catalogLogos(item),
    price: catalogDisplay(item),
    days: item.deliveryTime ?? '5-7 business days',
    ...auditCta(item, chip),
  }
}

export const SMART_AUDITS_HERO = {
  title: "Stop guessing what's wrong",
  titleEm: 'with the numbers.',
  sub: 'A fixed-price audit of your CRM, tracking, or ads. You get the findings, the cost to fix, and the fee back if you go ahead.',
  primaryCta: 'See all audits',
  primaryHref: '#menu',
  secondaryCta: 'Not sure which audit you need?',
  secondaryHref: '#get-quote',
} as const

const DJ_JUNK_CASE = {
  url: 'djsjunk.net',
  category: 'junk removal',
  stat: '3K+',
  statLabel: 'Jobs completed',
  footer:
    '"Provided a much needed service at a great price & is always great to work with.\nExtremely knowledgable & capable!"',
  logo: { src: '/images/testimonials/dj.png', alt: "DJ's Junk Removal" },
}

const HERO_CASE_URLS = ['denim.com', 'tripster.com', 'makesy.com'] as const

export const SMART_AUDITS_HERO_CASES = [
  DJ_JUNK_CASE,
  ...HERO_CASE_URLS.map((url) => {
    const match = caseStudies.find((item) => item.url === url)
    if (!match) throw new Error(`Missing case study for ${url}`)
    return match
  }),
]

export const SMART_AUDITS_FIT = {
  title: 'This is a good fit if',
  items: [
    'Your CRM runs sales, marketing, or service work every day',
    'Your reports do not match what your team sees in the CRM',
    'Your team keeps the real numbers in spreadsheets',
    'You have run the CRM for a year or more with no full review',
    'Leadership does not trust CRM data enough to decide with it',
    'You are growing and the CRM has to keep up',
  ],
} as const

export const SMART_AUDITS_DIRECTORY = {
  title: 'Select your audit',
  credit: 'Every audit includes a scoped, fixed-price quote for the fix. Audit fee credited if you go ahead.',
} as const

export const SMART_AUDITS_INCLUDED = {
  getTitle: 'What you get',
  getItems: [
    'A written report, every finding ranked by severity',
    'What to fix first, in order',
    'A fixed-price quote to do the work',
    'Yours to keep, whoever does the fix',
  ],
  sampleLabel: 'Want a free demo sample?',
  samplePlaceholder: 'Work email',
  sampleCta: 'Send sample',
  sampleSuccess: "I'll email you a sample audit.",
  creditLabel: 'Credit',
  credit: '$500 against services',
} as const

export const SMART_AUDITS_CRM = CRM_IDS.map(fromCatalog)
export const SMART_AUDITS_MARKETING = MARKETING_IDS.map(fromCatalog)

export const SMART_AUDITS_CRM_DIRECTORY: DirectoryItem[] = SMART_AUDITS_CRM.map(({ id, chip, logos }) => ({
  id,
  chip,
  logos,
}))

export const SMART_AUDITS_CHANNEL_DIRECTORY: DirectoryItem[] = SMART_AUDITS_MARKETING.map(({ id, chip, logos }) => ({
  id,
  chip,
  logos,
}))
